/**
 * Entity compliance and sales tax: the due-date rules for annual reports, franchise taxes and sales
 * tax returns, and the filings each registration owes.
 *
 * The presets below were researched in October 2026 from official state websites' search results;
 * the pages themselves could not be opened, so every preset starts unconfirmed. A firm confirms a
 * rule (Firm Settings › Compliance rules) after checking its source, and can change it. See
 * docs/accuracy/state-compliance-2026-10.json for what was found and what conflicts.
 *
 * Weekends and holidays: the business-day adjustment uses federal holidays (with DC's Emancipation
 * Day for DC), plus the Maryland and Virginia holidays the firm has added in Firm Settings › Rules
 * and rates (none until then). Fees and some due dates come from the firm's confirmed rules
 * (rule-settings.ts); the preset `fee` text keeps only what isn't a figure.
 */
import { nextBusinessDay } from "./due-dates";
import { previousBusinessDay } from "./payroll";

export const ENTITY_TYPES = {
  llc: "Limited liability company",
  corporation: "Corporation",
  lp: "Limited partnership",
  llp: "Limited liability partnership",
  gp: "General partnership",
  nonprofit: "Nonprofit corporation",
  sole_proprietor: "Sole proprietorship",
} as const;
export type EntityTypeKey = keyof typeof ENTITY_TYPES;

export const COMPLIANCE_STATES: Record<string, string> = { MD: "Maryland", VA: "Virginia", DC: "District of Columbia", DE: "Delaware" };

export type ComplianceScheduleRule =
  | { kind: "fixed"; month: number; day: number }
  | { kind: "anniversary_month_end" }
  | { kind: "biennial_fixed"; month: number; day: number }
  | { kind: "monthly_following"; day: number }
  | { kind: "quarterly_following"; day: number }
  | { kind: "annual_following"; month: number; day: number; periodEndMonth: number };
export type WeekendRule = "none" | "next_business_day" | "previous_business_day";
export type SalesTaxFrequency = "monthly" | "quarterly" | "annual";
export const FREQUENCY_WORDS: Record<SalesTaxFrequency, string> = { monthly: "monthly", quarterly: "quarterly", annual: "annually" };

export type CompliancePreset = {
  key: string;
  kind: "entity" | "sales_tax";
  jurisdiction: string;
  name: string;
  entityTypes: EntityTypeKey[];
  appliesTo: "domestic" | "foreign" | "any";
  frequency: SalesTaxFrequency | null;
  schedule: ComplianceScheduleRule;
  weekend: WeekendRule;
  fee: string;
  sourceUrl: string;
};

const entity = (p: Omit<CompliancePreset, "kind" | "frequency">): CompliancePreset => ({ ...p, kind: "entity", frequency: null });
const sales = (jurisdiction: string, frequency: SalesTaxFrequency, schedule: ComplianceScheduleRule, weekend: WeekendRule, sourceUrl: string): CompliancePreset => ({
  key: `${jurisdiction.toLowerCase()}_sales_${frequency}`,
  kind: "sales_tax",
  jurisdiction,
  name: `Sales and use tax return (${frequency === "annual" ? "annually" : frequency})`,
  entityTypes: [],
  appliesTo: "any",
  frequency,
  schedule,
  weekend,
  fee: "",
  sourceUrl,
});

export const COMPLIANCE_PRESETS: CompliancePreset[] = [
  entity({
    key: "md_annual_report",
    jurisdiction: "MD",
    name: "Annual Report and Business Personal Property Return (Form 1)",
    entityTypes: ["llc", "corporation", "lp", "llp", "nonprofit"],
    appliesTo: "any",
    schedule: { kind: "fixed", month: 4, day: 15 },
    weekend: "next_business_day",
    fee: "No fee for nonstock (nonprofit) corporations. A 60-day extension can be requested.",
    sourceUrl: "https://dat.maryland.gov/SiteAssets/Pages/sdatforms/2026%20Form%201%20Instructions%20%20FINAL.pdf",
  }),
  entity({
    key: "va_llc_registration_fee",
    jurisdiction: "VA",
    name: "Annual registration fee",
    entityTypes: ["llc", "lp"],
    appliesTo: "any",
    schedule: { kind: "anniversary_month_end" },
    weekend: "none",
    fee: "Limited partnerships are believed to pay the same as LLCs.",
    sourceUrl: "https://www.scc.virginia.gov/businesses/business-faqs/annual-registration-fees/",
  }),
  entity({
    key: "va_corporation_annual",
    jurisdiction: "VA",
    name: "Annual report and registration fee",
    entityTypes: ["corporation"],
    appliesTo: "any",
    schedule: { kind: "anniversary_month_end" },
    // The report must arrive before the last business day when the month ends on a weekend or holiday.
    weekend: "previous_business_day",
    fee: "Based on the number of authorized shares.",
    sourceUrl: "https://www.scc.virginia.gov/businesses/business-faqs/annual-registration-fees/",
  }),
  entity({
    key: "dc_biennial_report",
    jurisdiction: "DC",
    name: "Biennial report",
    entityTypes: ["llc", "corporation", "lp", "llp"],
    appliesTo: "any",
    schedule: { kind: "biennial_fixed", month: 4, day: 1 },
    weekend: "none",
    fee: "",
    sourceUrl: "https://dlcp.dc.gov/page/corporations-division-business-registration-faqs",
  }),
  entity({
    key: "dc_biennial_report_nonprofit",
    jurisdiction: "DC",
    name: "Biennial report (nonprofit)",
    entityTypes: ["nonprofit"],
    appliesTo: "any",
    schedule: { kind: "biennial_fixed", month: 4, day: 1 },
    weekend: "none",
    fee: "",
    sourceUrl: "https://dlcp.dc.gov/node/1621926",
  }),
  entity({
    key: "de_corporation_franchise",
    jurisdiction: "DE",
    name: "Annual franchise tax report",
    entityTypes: ["corporation"],
    appliesTo: "domestic",
    schedule: { kind: "fixed", month: 3, day: 1 },
    weekend: "none",
    fee: "Plus franchise tax.",
    sourceUrl: "https://corp.delaware.gov/paytaxes/",
  }),
  entity({
    key: "de_corporation_foreign_report",
    jurisdiction: "DE",
    name: "Annual report (foreign corporation)",
    entityTypes: ["corporation"],
    appliesTo: "foreign",
    schedule: { kind: "fixed", month: 6, day: 30 },
    weekend: "none",
    fee: "",
    sourceUrl: "https://corp.delaware.gov/paytaxes/",
  }),
  entity({
    key: "de_alternative_entity_tax",
    jurisdiction: "DE",
    name: "Annual tax (LLC, LP, GP)",
    entityTypes: ["llc", "lp", "gp"],
    appliesTo: "any",
    schedule: { kind: "fixed", month: 6, day: 1 },
    weekend: "none",
    fee: "",
    sourceUrl: "https://corp.delaware.gov/alt-entitytaxinstructions/",
  }),
  sales("MD", "monthly", { kind: "monthly_following", day: 20 }, "next_business_day", "https://www.marylandtaxes.gov/pros/deadlines-and-duedates.php"),
  sales("MD", "quarterly", { kind: "quarterly_following", day: 20 }, "next_business_day", "https://www.marylandtaxes.gov/pros/deadlines-and-duedates.php"),
  sales("VA", "monthly", { kind: "monthly_following", day: 20 }, "none", "https://www.tax.virginia.gov/retail-sales-and-use-tax"),
  sales("VA", "quarterly", { kind: "quarterly_following", day: 20 }, "none", "https://www.tax.virginia.gov/retail-sales-and-use-tax"),
  sales("DC", "monthly", { kind: "monthly_following", day: 20 }, "next_business_day", "https://otr.cfo.dc.gov/page/sales-and-use-taxes"),
  sales("DC", "quarterly", { kind: "quarterly_following", day: 20 }, "next_business_day", "https://otr.cfo.dc.gov/page/sales-and-use-taxes"),
  // DC's annual filers report the year ending September 30, due October 20.
  sales("DC", "annual", { kind: "annual_following", month: 10, day: 20, periodEndMonth: 9 }, "next_business_day", "https://otr.cfo.dc.gov/page/sales-and-use-taxes"),
];

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const pad = (n: number) => String(n).padStart(2, "0");
const lastDay = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const ymd = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(Math.min(d, lastDay(y, m)))}`;

/** The firm's own holidays for a state in effect on a statutory date (Maryland and Virginia; null when none). */
export type StateHolidays = (jurisdiction: string, statutoryDate: string) => readonly string[] | null;

function adjust(date: string, weekend: WeekendRule, jurisdiction?: string, holidays?: StateHolidays): string {
  const extra = new Set(jurisdiction ? (holidays?.(jurisdiction, date) ?? []) : []);
  // DC's Emancipation Day (April 16) is a holiday in DC only.
  if (weekend === "next_business_day") return nextBusinessDay(date, { irs: jurisdiction === "DC", extraHolidays: extra });
  if (weekend === "previous_business_day") {
    let d = previousBusinessDay(date);
    while (extra.has(d)) d = previousBusinessDay(new Date(Date.parse(`${d}T12:00:00Z`) - 86_400_000).toISOString().slice(0, 10));
    return d;
  }
  return date;
}

export type ComplianceDue = { periodKey: string; periodLabel: string; dueOn: string };

/**
 * The filings a registration owes under a rule, due between `from` and `to` (inclusive).
 *
 * Annual entity filings start the year after registration (the entity existed on January 1);
 * biennial ones the year after, then every second year. Sales tax periods start with the month or
 * quarter of registration; a registration that has ended owes nothing for periods starting after it.
 */
export function complianceDueDates(rule: { schedule: ComplianceScheduleRule; weekend: WeekendRule; jurisdiction?: string }, reg: { registeredOn: string; endsOn: string | null }, from: string, to: string, holidays?: StateHolidays): ComplianceDue[] {
  const out: ComplianceDue[] = [];
  const ry = Number(reg.registeredOn.slice(0, 4));
  const rm = Number(reg.registeredOn.slice(5, 7));
  const lastYear = Number(to.slice(0, 4)) + 1;
  const push = (periodKey: string, periodLabel: string, raw: string, periodStart: string) => {
    if (reg.endsOn && periodStart > reg.endsOn) return;
    const dueOn = adjust(raw, rule.weekend, rule.jurisdiction, holidays);
    if (dueOn >= from && dueOn <= to) out.push({ periodKey, periodLabel, dueOn });
  };
  const s = rule.schedule;
  switch (s.kind) {
    case "fixed":
      for (let y = ry + 1; y <= lastYear; y++) push(String(y), String(y), ymd(y, s.month, s.day), `${y}-01-01`);
      break;
    case "anniversary_month_end":
      for (let y = ry + 1; y <= lastYear; y++) push(String(y), String(y), ymd(y, rm, 31), `${y}-${pad(rm)}-01`);
      break;
    case "biennial_fixed":
      for (let y = ry + 1; y <= lastYear; y += 2) push(String(y), String(y), ymd(y, s.month, s.day), `${y}-01-01`);
      break;
    case "monthly_following":
      for (let y = ry, m = rm; y < lastYear || (y === lastYear && m <= 12); m === 12 ? ((m = 1), y++) : m++) {
        const [dy, dm] = m === 12 ? [y + 1, 1] : [y, m + 1];
        push(`${y}-${pad(m)}`, `${MONTHS[m - 1]} ${y}`, ymd(dy, dm, s.day), `${y}-${pad(m)}-01`);
      }
      break;
    case "quarterly_following":
      for (let y = ry, q = Math.ceil(rm / 3); y <= lastYear; q === 4 ? ((q = 1), y++) : q++) {
        const endMonth = q * 3;
        const [dy, dm] = endMonth === 12 ? [y + 1, 1] : [y, endMonth + 1];
        push(`${y}-Q${q}`, `Q${q} ${y}`, ymd(dy, dm, s.day), `${y}-${pad(endMonth - 2)}-01`);
      }
      break;
    case "annual_following":
      // Periods end in periodEndMonth; the first is the one the registration falls in.
      for (let endYear = rm <= s.periodEndMonth ? ry : ry + 1; endYear <= lastYear; endYear++) {
        const dueYear = s.month > s.periodEndMonth ? endYear : endYear + 1;
        const startMonth = (s.periodEndMonth % 12) + 1;
        const startYear = s.periodEndMonth === 12 ? endYear : endYear - 1;
        push(`FY${endYear}`, `Year ending ${MONTHS[s.periodEndMonth - 1]} ${endYear}`, ymd(dueYear, s.month, s.day), `${startYear}-${pad(startMonth)}-01`);
      }
      break;
  }
  return out;
}

/**
 * Due dates when the firm has corrected a rule's month and day: each period uses the newest version
 * whose effective date is on or before the due date that version gives, otherwise the first
 * (`versions[0]`, from: null). Filings already made keep the date they were made with.
 */
export function complianceDueDatesVersioned(
  rule: { weekend: WeekendRule; jurisdiction?: string },
  versions: Array<{ schedule: ComplianceScheduleRule; from: string | null }>,
  reg: { registeredOn: string; endsOn: string | null },
  from: string,
  to: string,
  holidays?: StateHolidays,
): ComplianceDue[] {
  // Oldest first, so a later version replaces an earlier one for the periods it covers. A version
  // can move a date across the window's edges, so each is worked out over a wider span.
  const sorted = [...versions].sort((a, b) => (a.from ?? "").localeCompare(b.from ?? ""));
  const wideTo = `${Number(to.slice(0, 4)) + 2}-12-31`;
  const periods = new Map<string, ComplianceDue>();
  for (const v of sorted) {
    for (const d of complianceDueDates({ ...rule, schedule: v.schedule }, reg, "0000-01-01", wideTo, holidays)) if (v.from === null || d.dueOn >= v.from) periods.set(d.periodKey, d);
  }
  return [...periods.values()].filter((d) => d.dueOn >= from && d.dueOn <= to).sort((a, b) => (a.dueOn < b.dueOn ? -1 : a.dueOn > b.dueOn ? 1 : 0));
}

/** Which rules a registration follows: entity rules by legal form and domestic or foreign; sales tax rules by frequency. */
export function rulesFor<R extends { kind: string; jurisdiction: string; entityTypes: string[]; appliesTo: string; frequency: string | null; active: boolean }>(
  rules: R[],
  reg: { kind: string; jurisdiction: string; scope: string | null; frequency: string | null },
  entityType: string | null,
): R[] {
  return rules.filter((r) => {
    if (!r.active || r.kind !== reg.kind || r.jurisdiction !== reg.jurisdiction) return false;
    if (r.kind === "sales_tax") return r.frequency === reg.frequency;
    return !!entityType && r.entityTypes.includes(entityType) && (r.appliesTo === "any" || r.appliesTo === reg.scope);
  });
}
